// Sopwith 3D - placeholder-free procedural models.
//
// Every model is built from primitives, centred on the sprite's centre and
// facing +X, and sized to the 16x16 sprite so the picture matches the
// simulation's collision boxes. Toon materials and hull outlines give them
// the cartoon look.

import * as THREE from 'three';
import { TargetType } from '../sim/types';
import { COLOR_MAPPINGS } from './framebuffer';
import { toon } from './materials';

/** 3D colours standing in for the CGA indices: cyan, magenta, white. */
export const CGA3D = [
  new THREE.Color(0x000000),
  new THREE.Color(0x2ec4b6),
  new THREE.Color(0xd94f8a),
  new THREE.Color(0xf4efe3),
];

export function factionColors(clr: number): {
  body: THREE.Color;
  wing: THREE.Color;
  trim: THREE.Color;
} {
  const m = COLOR_MAPPINGS[clr] ?? COLOR_MAPPINGS[0];
  return { body: CGA3D[m[1]], wing: CGA3D[m[2]], trim: CGA3D[m[3]] };
}

const matCache = new Map<number, THREE.MeshToonMaterial>();
function mat(color: THREE.Color | number): THREE.MeshToonMaterial {
  const hex = typeof color === 'number' ? color : color.getHex();
  let m = matCache.get(hex);
  if (!m) {
    m = toon(hex);
    matCache.set(hex, m);
  }
  return m;
}

export const SHARED = {
  dark: mat(0x2b2b2b),
  metal: mat(0x8d949c),
  wood: mat(0x8a6a3c),
  canvas: mat(0xd9cbb3),
  rust: mat(0x9a4a2a),
  bird: mat(0x4a3b2f),
  birdBelly: mat(0xd9cbb3),
  beak: mat(0xe0a030),
  ox: mat(0x8a5a3c),
  oxLight: mat(0xc9a27a),
  bomb: mat(0x333333),
  bullet: new THREE.MeshBasicMaterial({ color: new THREE.Color(2.2, 1.9, 1.1), toneMapped: false }),
  fire: new THREE.MeshBasicMaterial({ color: new THREE.Color(2.4, 1.1, 0.3), toneMapped: false }),
  ember: mat(0x5a3020),
  smoke: new THREE.MeshBasicMaterial({ color: 0x8a8a8a, transparent: true, opacity: 0.55 }),
  prop: new THREE.MeshBasicMaterial({ color: 0x222222, transparent: true, opacity: 0.35 }),
};

function box(w: number, h: number, d: number, m: THREE.Material, x = 0, y = 0, z = 0): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
  mesh.position.set(x, y, z);
  return mesh;
}

function cyl(
  r: number,
  h: number,
  m: THREE.Material,
  x = 0,
  y = 0,
  z = 0,
  seg = 12,
): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, seg), m);
  mesh.position.set(x, y, z);
  return mesh;
}

// ---------------------------------------------------------------------------
// Biplane

export function buildPlane(clr: number): THREE.Group {
  const g = new THREE.Group();
  const { body, wing, trim } = factionColors(clr);
  const bodyM = mat(body);
  const wingM = mat(wing);
  const trimM = mat(trim);

  // Fuselage: tapered from the cowl to the tail.
  const fuselage = new THREE.Mesh(new THREE.BoxGeometry(13, 3.2, 3.2), bodyM);
  fuselage.position.set(-0.5, -0.8, 0);
  const pos = fuselage.geometry.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    if (x < 0) {
      const t = 1 + (x / 6.5) * 0.55; // narrow toward the tail
      pos.setY(i, pos.getY(i) * t);
      pos.setZ(i, pos.getZ(i) * t);
    }
  }
  pos.needsUpdate = true;
  fuselage.geometry.computeVertexNormals();

  const cowl = cyl(1.8, 2.2, SHARED.metal, 6.4, -0.8, 0, 10);
  cowl.rotation.z = Math.PI / 2;
  const headrest = box(1.6, 1.2, 1.6, bodyM, -2.2, 1.2, 0);

  // Wings with a hint of dihedral, plus struts and wheels.
  const upper = box(5.5, 0.5, 19, wingM, 1.2, 3.4, 0);
  const lower = box(5.5, 0.5, 16, wingM, 1.2, -2.4, 0);
  for (const z of [-5.5, 5.5]) {
    g.add(box(0.5, 5.6, 0.5, trimM, -0.6, 0.5, z), box(0.5, 5.6, 0.5, trimM, 3.0, 0.5, z));
  }
  const tailplane = box(4, 0.4, 7.5, wingM, -6.5, 0.2, 0);
  const fin = new THREE.Mesh(new THREE.BoxGeometry(3.2, 3.6, 0.5), bodyM);
  fin.position.set(-6.4, 2.0, 0);
  const rudder = box(1.4, 3.0, 0.4, trimM, -8.0, 1.6, 0);

  const axle = box(0.4, 0.4, 5.4, SHARED.dark, 2.2, -4.2, 0);
  for (const z of [-2.5, 2.5]) {
    const wheel = cyl(1.3, 0.6, SHARED.dark, 2.2, -4.2, z, 10);
    wheel.rotation.x = Math.PI / 2;
    g.add(wheel);
  }
  const legs = box(0.4, 2.2, 4.8, SHARED.dark, 2.2, -3.0, 0);

  // Propeller: a blade pair that spins, and a translucent disc when running.
  const prop = new THREE.Group();
  prop.name = 'prop';
  prop.position.set(7.6, -0.8, 0);
  prop.add(box(0.3, 7.2, 0.9, SHARED.dark), box(0.2, 0.2, 0.2, SHARED.metal));
  const disc = new THREE.Mesh(new THREE.CircleGeometry(3.6, 20), SHARED.prop);
  disc.name = 'propdisc';
  disc.rotation.y = Math.PI / 2;
  disc.position.set(7.7, -0.8, 0);

  g.add(fuselage, cowl, headrest, upper, lower, tailplane, fin, rudder, axle, legs, prop, disc);
  return g;
}

// ---------------------------------------------------------------------------
// Ground targets (w, h are the sprite size; the model sits between y=-h/2 and y=h/2)

export function buildTarget(kind: number, clr: number, w: number, h: number): THREE.Group {
  const g = new THREE.Group();
  const { body, wing, trim } = factionColors(clr);
  const bodyM = mat(body);
  const wingM = mat(wing);
  const trimM = mat(trim);
  const bottom = -h / 2;

  switch (kind) {
    case TargetType.HANGAR: {
      // Half-cylinder roof on low walls, doors open at the front.
      const roof = new THREE.Mesh(
        new THREE.CylinderGeometry(w / 2, w / 2, 14, 16, 1, false, 0, Math.PI),
        wingM,
      );
      roof.rotation.z = Math.PI / 2;
      roof.rotation.y = Math.PI / 2;
      roof.position.y = bottom + 3;
      const walls = box(w, 3, 14, bodyM, 0, bottom + 1.5, 0);
      const pole = box(0.5, h, 0.5, SHARED.dark, w / 2 - 4, 0, -5);
      const flag = box(4, 2.4, 0.3, trimM, w / 2 - 1.8, h / 2 - 1.5, -5);
      g.add(roof, walls, pole, flag);
      break;
    }
    case TargetType.FACTORY: {
      const hall = box(w, h * 0.55, 14, bodyM, 0, bottom + h * 0.275, 0);
      // Saw-tooth roof.
      for (let i = 0; i < 3; i++) {
        const tooth = new THREE.Mesh(new THREE.ConeGeometry(2.6, 3, 4), wingM);
        tooth.rotation.y = Math.PI / 4;
        tooth.position.set(-w / 2 + 2.7 + i * 5.3, bottom + h * 0.55 + 1.5, 0);
        tooth.scale.set(1, 1, 2.6);
        g.add(tooth);
      }
      const chimney = cyl(1.1, h * 0.7, SHARED.rust, w / 2 - 2, bottom + h * 0.35 + 2, -4, 8);
      g.add(hall, chimney);
      break;
    }
    case TargetType.OIL_TANK: {
      const tank = cyl(w / 2 - 1, h * 0.8, trimM, 0, bottom + h * 0.4, 0, 18);
      const cap = new THREE.Mesh(new THREE.SphereGeometry(w / 2 - 1, 18, 8, 0, Math.PI * 2, 0, Math.PI / 2), trimM);
      cap.position.y = bottom + h * 0.8;
      cap.scale.y = 0.35;
      const band = cyl(w / 2 - 0.7, 0.8, SHARED.dark, 0, bottom + h * 0.4, 0, 18);
      g.add(tank, cap, band);
      break;
    }
    case TargetType.TANK: {
      const tracks = box(w, 4, 8, SHARED.dark, 0, bottom + 2, 0);
      const hull = box(w - 2, 3.5, 7, bodyM, 0, bottom + 5.5, 0);
      const turret = box(6, 3, 5, wingM, -1, bottom + 8.7, 0);
      const barrel = box(8, 0.9, 0.9, SHARED.dark, 5, bottom + 9.2, 0);
      g.add(tracks, hull, turret, barrel);
      break;
    }
    case TargetType.TRUCK:
    case TargetType.TANKER_TRUCK: {
      const chassis = box(w - 1, 2, 6, SHARED.dark, 0, bottom + 2.5, 0);
      const cab = box(4.5, 5, 6, bodyM, w / 2 - 3, bottom + 5.5, 0);
      const load =
        kind === TargetType.TANKER_TRUCK
          ? (() => {
              const c = cyl(2.8, 9, trimM, -2.5, bottom + 6, 0, 12);
              c.rotation.z = Math.PI / 2;
              return c;
            })()
          : box(9, 5, 6.2, wingM, -2.5, bottom + 5.5, 0);
      for (const x of [-5, 4.5]) {
        for (const z of [-3, 3]) {
          const wheel = cyl(1.5, 1, SHARED.dark, x, bottom + 1.5, z, 10);
          wheel.rotation.x = Math.PI / 2;
          g.add(wheel);
        }
      }
      g.add(chassis, cab, load);
      break;
    }
    case TargetType.FLAG: {
      const pole = box(0.7, h, 0.7, SHARED.dark, -w / 2 + 2, 0, 0);
      const flag = box(w * 0.55, h * 0.3, 0.3, bodyM, -w / 2 + 2 + w * 0.275, h / 2 - h * 0.15, 0);
      g.add(pole, flag);
      break;
    }
    case TargetType.TENT: {
      const tent = new THREE.Mesh(new THREE.ConeGeometry(w / 2, h * 0.8, 3), SHARED.canvas);
      tent.rotation.y = Math.PI / 2;
      tent.scale.z = 1.3;
      tent.position.y = bottom + h * 0.4;
      g.add(tent);
      break;
    }
    case TargetType.RADIO_TOWER: {
      for (const [x, z] of [
        [-2, -2],
        [2, -2],
        [-2, 2],
        [2, 2],
      ]) {
        g.add(box(0.5, h, 0.5, SHARED.metal, x * 0.8, 0, z * 0.8));
      }
      g.add(box(3.6, 0.4, 3.6, SHARED.metal, 0, 0, 0), box(3.6, 0.4, 3.6, SHARED.metal, 0, h / 2 - 1, 0));
      const beacon = new THREE.Mesh(new THREE.SphereGeometry(0.8, 8, 8), SHARED.fire);
      beacon.position.y = h / 2;
      g.add(beacon);
      break;
    }
    case TargetType.WATER_TOWER: {
      for (const [x, z] of [
        [-3, -3],
        [3, -3],
        [-3, 3],
        [3, 3],
      ]) {
        g.add(box(0.6, h * 0.55, 0.6, SHARED.wood, x, bottom + h * 0.275, z));
      }
      const tub = cyl(w / 2 - 1, h * 0.4, SHARED.wood, 0, bottom + h * 0.75, 0, 14);
      const lid = new THREE.Mesh(new THREE.ConeGeometry(w / 2, 3, 14), trimM);
      lid.position.y = bottom + h * 0.95 + 1.5;
      g.add(tub, lid);
      break;
    }
    default: {
      const b = box(w, h * 0.75, 14, bodyM, 0, bottom + h * 0.375, 0);
      const roof = box(w * 1.05, h * 0.25, 15, wingM, 0, bottom + h * 0.875, 0);
      g.add(b, roof);
      break;
    }
  }
  return g;
}

export function buildRubble(w: number, h: number): THREE.Group {
  const g = new THREE.Group();
  const bottom = -h / 2;
  g.add(box(w * 0.9, h * 0.22, 11, SHARED.dark, 0, bottom + h * 0.11, 0));
  g.add(box(w * 0.4, h * 0.3, 6, SHARED.rust, -w * 0.2, bottom + h * 0.25, 2));
  g.add(box(w * 0.25, h * 0.4, 5, SHARED.dark, w * 0.25, bottom + h * 0.3, -2));
  return g;
}

// ---------------------------------------------------------------------------
// Animals

export function buildOx(): THREE.Group {
  const g = new THREE.Group();
  g.add(
    box(10, 6, 5, SHARED.ox, 0, -1, 0),
    box(8, 2, 5.2, SHARED.oxLight, 0, -3, 0),
    box(4, 4, 4, SHARED.ox, 6.5, 1, 0),
    box(1, 1, 3, SHARED.oxLight, 6.5, 3.5, 1.8),
    box(1, 1, 3, SHARED.oxLight, 6.5, 3.5, -1.8),
  );
  for (const [x, z] of [
    [-3.5, 1.5],
    [-3.5, -1.5],
    [3, 1.5],
    [3, -1.5],
  ]) {
    g.add(box(1.5, 4, 1.5, SHARED.ox, x, -6, z));
  }
  return g;
}

/** A bird facing +X with two wing pivots named 'wing' for flapping. */
export function buildBird(scale = 1): THREE.Group {
  const g = new THREE.Group();
  const bodyM = new THREE.Mesh(new THREE.SphereGeometry(1.3, 8, 6), SHARED.bird);
  bodyM.scale.set(1.6, 0.8, 0.9);
  const belly = new THREE.Mesh(new THREE.SphereGeometry(1.0, 8, 6), SHARED.birdBelly);
  belly.scale.set(1.3, 0.6, 0.8);
  belly.position.y = -0.5;
  const beak = new THREE.Mesh(new THREE.ConeGeometry(0.5, 1.4, 4), SHARED.beak);
  beak.rotation.z = -Math.PI / 2;
  beak.position.x = 2.6;
  for (const side of [1, -1]) {
    const pivot = new THREE.Group();
    pivot.name = 'wing';
    pivot.position.set(-0.2, 0.4, side * 0.6);
    pivot.userData.side = side;
    const wing = box(2.4, 0.25, 3.2, SHARED.bird, 0, 0, side * 1.7);
    pivot.add(wing);
    g.add(pivot);
  }
  g.add(bodyM, belly, beak);
  g.scale.setScalar(scale);
  return g;
}

export function buildFlock(): THREE.Group {
  const g = new THREE.Group();
  const offsets = [
    [4, 1, 0],
    [0, 0, 3],
    [0, 0, -3],
    [-4, -1, 6],
    [-4, -1, -6],
  ];
  offsets.forEach(([x, y, z], i) => {
    const b = buildBird(0.9);
    b.position.set(x, y, z);
    b.userData.phase = i * 1.1;
    g.add(b);
  });
  return g;
}

// ---------------------------------------------------------------------------
// Ordnance and effects

export function buildBomb(): THREE.Group {
  const g = new THREE.Group();
  const bodyM = new THREE.Mesh(new THREE.CapsuleGeometry(1.1, 3.2, 4, 8), SHARED.bomb);
  bodyM.rotation.z = Math.PI / 2;
  g.add(bodyM, box(0.3, 2.4, 2.4, SHARED.metal, -2.4, 0, 0));
  return g;
}

export function buildMissile(): THREE.Group {
  const g = new THREE.Group();
  const bodyM = new THREE.Mesh(new THREE.CapsuleGeometry(0.8, 5, 4, 8), SHARED.metal);
  bodyM.rotation.z = Math.PI / 2;
  const flame = new THREE.Mesh(new THREE.ConeGeometry(0.9, 2.5, 8), SHARED.fire);
  flame.rotation.z = Math.PI / 2;
  flame.position.x = -4;
  g.add(bodyM, flame);
  return g;
}

export function buildShot(): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.CapsuleGeometry(0.35, 2.2, 3, 6), SHARED.bullet);
  m.rotation.z = Math.PI / 2;
  return m;
}

export function buildBalloon(clr: number): THREE.Group {
  const g = new THREE.Group();
  const { body } = factionColors(clr);
  const env = new THREE.Mesh(new THREE.SphereGeometry(6.5, 14, 12), mat(body));
  env.position.y = 2;
  const basket = box(3, 2, 3, SHARED.wood, 0, -6, 0);
  g.add(env, basket);
  for (const z of [-1, 1]) {
    g.add(box(0.15, 4, 0.15, SHARED.dark, -1, -3.5, z), box(0.15, 4, 0.15, SHARED.dark, 1, -3.5, z));
  }
  return g;
}

/** One piece of explosion debris: a glowing chunk that darkens as it falls. */
export function buildDebris(): THREE.Mesh {
  return new THREE.Mesh(new THREE.BoxGeometry(1.6, 1.6, 1.6), SHARED.fire);
}

export function buildStarburst(): THREE.Mesh {
  return new THREE.Mesh(new THREE.SphereGeometry(1.6, 8, 8), SHARED.bullet);
}

export function buildSmoke(): THREE.Mesh {
  return new THREE.Mesh(new THREE.SphereGeometry(1.8, 7, 6), SHARED.smoke);
}
